import React, { useState, useEffect, useMemo } from 'react';
import { Carousel, Row, Col } from 'antd';
import Image from 'next/image';
import { useRouter } from 'next/router';
import styles from './locationsection.module.scss';
import { Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { useMediaQuery } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'next-i18next';

// Styled components
const SectionTitle = styled(Typography)({
  fontSize: '33px',
  fontWeight: 'bold',
  marginBottom: '24px',
});

const ViewAllButton = styled(Button)({
  fontSize: '16px',
  color: '#333',
  textTransform: 'none',
  float: 'right',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

// Tablet-specific styled components
const createItemCard = (isTablet: boolean, isMobile: boolean) => styled(Box)({
  margin: isMobile ? '0 4px' : (isTablet ? '0 4px' : '0 8px'),
  borderRadius: '10px',
  overflow: 'hidden',
  position: 'relative',
});

const createImageContainer = (isTablet: boolean, isMobile: boolean) => styled(Box)({
  overflow: 'hidden',
  borderRadius: '10px',
  position: 'relative',
  width: '100%',
  aspectRatio: isMobile ? '4/3' : 'auto', // 4:3 aspect ratio สำหรับ mobile
  height: isMobile ? 'auto' : (isTablet ? '140px' : '180px'), // auto height สำหรับ mobile เพื่อใช้ aspect ratio
});

const createItemContent = (isTablet: boolean, isMobile: boolean) => styled(Box)({
  padding: isMobile ? '10px' : (isTablet ? '12px' : '16px'), // ลด padding ใน mobile และ tablet
  textAlign: 'center',
  backgroundColor: 'transparent',
});

const createItemTitle = (isTablet: boolean, isMobile: boolean) => styled(Typography)({
  fontSize: isMobile ? '16px' : (isTablet ? '18px' : '23px'), // ลดขนาดฟอนต์ใน mobile และ tablet
  fontWeight: '500',
  marginBottom: isMobile ? '4px' : (isTablet ? '6px' : '8px'),
});

const createItemCount = (isTablet: boolean, isMobile: boolean) => styled(Typography)({
  fontSize: isMobile ? '14px' : (isTablet ? '16px' : '19px'), // ลดขนาดฟอนต์ใน mobile และ tablet
  fontWeight: '500',
  '& .count': {
    color: '#F4762A',
    fontWeight: '500',
  },
});

const ItemImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

const StyledCarousel = styled(Carousel)({
  '.slick-dots': {
    bottom: '-35px',
  },
  '.slick-dots li button': {
    backgroundColor: 'rgba(211, 211, 211, 1)',
    borderRadius: '50%',
    width: '10px',
    height: '10px',
    '&:before': {
      content: '""',
    },
  },
  '.slick-dots li.slick-active button': {
    backgroundColor: 'rgba(244, 118, 42, 1)',
    width: '10px',
    height: '10px',
  },
  '.slick-prev, .slick-next': {
    display: 'none !important',
  },
});

const createStyledAccordion = (isTablet: boolean) => styled(Accordion)({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  fontFamily: "'Prompt', 'Inter', 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
  marginBottom: isTablet ? '6px' : '8px', // ลดระยะห่างระหว่าง accordion
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    backgroundColor: '#F5F5F6',
    borderRadius: '8px',
    minHeight: isTablet ? '40px' : '44px', // ลดความสูงจาก 48px
    fontFamily: "'Prompt', 'Inter', 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
    padding: isTablet ? '0 12px' : '0 16px', // ลด padding
    '&.Mui-expanded': {
      minHeight: isTablet ? '40px' : '44px',
    }
  },
  '& .MuiAccordionSummary-content': {
    margin: isTablet ? '8px 0' : '10px 0', // ลด margin จาก 12px
    fontFamily: "'Prompt', 'Inter', 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
    '&.Mui-expanded': {
      margin: isTablet ? '8px 0' : '10px 0',
    },
    '& .MuiTypography-root': {
      fontSize: isTablet ? '15px' : '16px', // ลดขนาดฟอนต์
      fontWeight: '500',
    }
  },
  '& .MuiAccordionDetails-root': {
    padding: isTablet ? '8px' : '10px 12px', // ลด padding
    fontFamily: "'Prompt', 'Inter', 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
  },
  '& .MuiTypography-root': {
    fontFamily: "'Prompt', 'Inter', 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
  }
});

const createStyledChip = (isTablet: boolean) => styled(Chip)({
  cursor: 'pointer',
  padding: isTablet ? '6px' : '8px',
  fontSize: isTablet ? '13px' : '14px',
  borderRadius: '5px',
  backgroundColor: '#fff',
  color: '#646464',
  fontFamily: "'Prompt', 'Inter', 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
  height: isTablet ? '30px' : '34px', // ลดความสูงของ chip
  '&:hover': {
    backgroundColor: '#f4762a',
    color: '#fff',
    '& .MuiSvgIcon-root': {
      color: '#fff',
    }
  },
  '& .MuiChip-label': {
    padding: isTablet ? '6px' : '8px',
    fontFamily: "'Prompt', 'Inter', 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important",
    fontSize: isTablet ? '13px' : '14px',
  },
  '& .MuiSvgIcon-root': {
    fontSize: isTablet ? '10px' : '12px', // ลดขนาด icon
  }
});

interface LocationItemProps {
  id: number;
  name: string;
  image: string;
  count: number;
}

interface RegionProps {
  name: string;
  provinces: {
    id: number;
    name: string;
    link: string;
  }[];
  image: string;
}

// เพิ่ม interface สำหรับ props
interface LocationSectionProps {
  popularLocationsData?: Array<{
    name: string;
    nameEN: string;
    nameCN: string;
    provinceId: number;
    count: number | string;
  }>;
  isTablet?: boolean;
  isMobile?: boolean;
}

const LocationSection: React.FC<LocationSectionProps> = ({ popularLocationsData, isTablet = false, isMobile = false }) => {
  const router = useRouter();
  const isDesktop = useMediaQuery('(min-width:1025px)');
  const [expanded, setExpanded] = useState<string | false>('0');
  const { locale } = router;
  const { t } = useTranslation('common');



  // สร้าง click handler สำหรับ popular locations
  const handleLocationClick = (locationKey: string | null, provinceName: string) => {
    const link = buildLocationLink(provinceName, locationKey || undefined);
    router.push(link);
  };

  // Update expanded state when screen size changes
  useEffect(() => {
    if (isDesktop && expanded === false) {
      setExpanded('0');
    }
  }, [isDesktop, expanded]);

  // Create dynamic styled components based on tablet mode
  const ItemCard = createItemCard(isTablet, isMobile);
  const ImageContainer = createImageContainer(isTablet, isMobile);
  const ItemContent = createItemContent(isTablet, isMobile);
  const ItemTitle = createItemTitle(isTablet, isMobile);
  const ItemCount = createItemCount(isTablet, isMobile);
  const StyledAccordion = createStyledAccordion(isTablet);
  const StyledChip = createStyledChip(isTablet);

  // ฟังก์ชันสำหรับหาจำนวนประกาศตาม provinceId
  const getCountByProvinceName = (provinceName: string): number => {
    if (!popularLocationsData || !Array.isArray(popularLocationsData)) return 0;
    
    const item = popularLocationsData.find(item => {
      switch(locale) {
        case 'en':
          return item.nameEN.toLowerCase() === provinceName.toLowerCase();
        case 'cn':
          return item.nameCN === provinceName;
        default:
          return item.name === provinceName;
      }
    });
    
    return item ? parseInt(item.count.toString()) || 0 : 0;
  };

  // URL path mappings สำหรับพื้นที่เฉพาะ
  const getLocationPath = (locationKey: string) => {
    const pathMappings: { [key: string]: { [locale: string]: string } } = {
      'huahin': {
        'th': 'ประจวบคีรีขันธ์/หัวหิน',
        'en': 'Prachuap-Khiri-Khan/Hua-Hin',
        'cn': '巴蜀/华欣'
      },
      'chaam': {
        'th': 'เพชรบุรี/ชะอำ',
        'en': 'Phetchaburi/Cha-am',
        'cn': '碧武里/差安'
      },
      'pattaya': {
        'th': 'ชลบุรี/บางละมุง/เมืองพัทยา',
        'en': 'Chonburi/Bang-Lamung/Mueang-Pattaya',
        'cn': '春武里/邦拉蒙/芭堤雅'
      },
      'sriracha': {
        'th': 'ชลบุรี/ศรีราชา',
        'en': 'Chonburi/Si-Racha',
        'cn': '春武里/是拉差'
      },
      'sattahip': {
        'th': 'ชลบุรี/สัตหีบ',
        'en': 'Chonburi/Sattahip',
        'cn': '春武里/梭桃邑'
      },
      'bangsaen': {
        'th': 'บางแสน',
        'en': 'Bang-Saen',
        'cn': '邦盛'
      },
      'jomtien': {
        'th': 'จอมเทียน',
        'en': 'Jomtien',
        'cn': '宗天'
      }
    };

    return pathMappings[locationKey]?.[locale || 'th'] || locationKey;
  };

  // สร้างลิงก์ไปยังหน้าค้นหาตามชื่อจังหวัด
  const buildLocationLink = (province: string, locationKey?: string | null) => {
    let actionText = 'ขาย';
    let categoryText = 'อสังหา';
    
    if (locale === 'en') {
      actionText = 'sell';
      categoryText = 'Estate';
    } else if (locale === 'cn') {
      actionText = '售';
      categoryText = '地产';
    }
    
    // ใช้ locationKey ถ้ามี ไม่งั้นใช้ province
    const locationPath = locationKey ? getLocationPath(locationKey) : province;
    
    return `/${actionText}/${categoryText}/${locationPath}?endPrice=1000000000`;
  };

  const regions: RegionProps[] = [
    {
      name: t('locationSection.regions.central'),
      image: '/assets/homePage/location/central.webp',
      provinces: [
        { id: 1, name: t('locationSection.provinces.bangkok'), link: buildLocationLink('กรุงเทพมหานคร') },
        { id: 2, name: t('locationSection.provinces.pathumthani'), link: buildLocationLink('ปทุมธานี') },
        { id: 3, name: t('locationSection.provinces.samutprakan'), link: buildLocationLink('สมุทรปราการ') },
        { id: 4, name: t('locationSection.provinces.nonthaburi'), link: buildLocationLink('นนทบุรี') },
        { id: 5, name: t('locationSection.provinces.nakhonpathom'), link: buildLocationLink('นครปฐม') },
        { id: 6, name: t('locationSection.provinces.ayutthaya'), link: buildLocationLink('พระนครศรีอยุธยา') },
        { id: 7, name: t('locationSection.provinces.samutsakhon'), link: buildLocationLink('สมุทรสาคร') },
        { id: 8, name: t('locationSection.provinces.nakhonsawan'), link: buildLocationLink('นครสวรรค์') },
        { id: 9, name: t('locationSection.provinces.huahin'), link: buildLocationLink('หัวหิน', 'huahin') },
        { id: 10, name: t('locationSection.provinces.chaam'), link: buildLocationLink('ชะอำ', 'chaam') }
      ]
    },
    {
      name: t('locationSection.regions.eastern'),
      image: '/assets/homePage/location/eastern.webp',
      provinces: [
        { id: 11, name: t('locationSection.provinces.chonburi'), link: buildLocationLink('ชลบุรี') },
        { id: 13, name: t('locationSection.provinces.sriracha'), link: buildLocationLink('ศรีราชา', 'sriracha') },
        { id: 14, name: t('locationSection.provinces.bangsaen'), link: buildLocationLink('บางแสน', 'bangsaen') },
        { id: 16, name: t('locationSection.provinces.sattahip'), link: buildLocationLink('สัตหีบ', 'sattahip') },
        { id: 17, name: t('locationSection.provinces.rayong'), link: buildLocationLink('ระยอง') },
        { id: 18, name: t('locationSection.provinces.chachoengsao'), link: buildLocationLink('ฉะเชิงเทรา') },
        { id: 19, name: t('locationSection.provinces.prachinburi'), link: buildLocationLink('ปราจีนบุรี') },
        { id: 20, name: t('locationSection.provinces.chanthaburi'), link: buildLocationLink('จันทบุรี') }
      ]
    },
    {
      name: t('locationSection.regions.northern'),
      image: '/assets/homePage/location/northern.webp',
      provinces: [
        { id: 21, name: t('locationSection.provinces.chiangmai'), link: buildLocationLink('เชียงใหม่') },
        { id: 22, name: t('locationSection.provinces.chiangrai'), link: buildLocationLink('เชียงราย') },
        { id: 23, name: t('locationSection.provinces.phitsanulok'), link: buildLocationLink('พิษณุโลก') },
        { id: 24, name: t('locationSection.provinces.lampang'), link: buildLocationLink('ลำปาง') },
        { id: 25, name: t('locationSection.provinces.phayao'), link: buildLocationLink('พะเยา') },
        { id: 26, name: t('locationSection.provinces.lamphun'), link: buildLocationLink('ลำพูน') },
        { id: 27, name: t('locationSection.provinces.phichit'), link: buildLocationLink('พิจิตร') },
        { id: 28, name: t('locationSection.provinces.sukhothai'), link: buildLocationLink('สุโขทัย') }
      ]
    },
    {
      name: t('locationSection.regions.northeastern'),
      image: '/assets/homePage/location/northeastern.webp',
      provinces: [
        { id: 29, name: t('locationSection.provinces.nakhonratchasima'), link: buildLocationLink('นครราชสีมา') },
        { id: 30, name: t('locationSection.provinces.khonkaen'), link: buildLocationLink('ขอนแก่น') },
        { id: 31, name: t('locationSection.provinces.udonthani'), link: buildLocationLink('อุดรธานี') },
        { id: 32, name: t('locationSection.provinces.amnatcharoen'), link: buildLocationLink('อำนาจเจริญ') },
        { id: 33, name: t('locationSection.provinces.roiet'), link: buildLocationLink('ร้อยเอ็ด') },
        { id: 34, name: t('locationSection.provinces.buriram'), link: buildLocationLink('บุรีรัมย์') },
        { id: 35, name: t('locationSection.provinces.sisaket'), link: buildLocationLink('ศรีสะเกษ') },
        { id: 36, name: t('locationSection.provinces.chaiyaphum'), link: buildLocationLink('ชัยภูมิ') },
        { id: 37, name: t('locationSection.provinces.mahasarakham'), link: buildLocationLink('มหาสารคาม') },
        { id: 38, name: t('locationSection.provinces.loei'), link: buildLocationLink('เลย') }
      ]
    },
    {
      name: t('locationSection.regions.southern'),
      image: '/assets/homePage/location/southern.webp',
      provinces: [
        { id: 39, name: t('locationSection.provinces.songkhla'), link: buildLocationLink('สงขลา') },
        { id: 40, name: t('locationSection.provinces.nakhonsithammarat'), link: buildLocationLink('นครศรีธรรมราช') },
        { id: 41, name: t('locationSection.provinces.suratthani'), link: buildLocationLink('สุราษฎร์ธานี') },
        { id: 42, name: t('locationSection.provinces.phuket'), link: buildLocationLink('ภูเก็ต') },
        { id: 43, name: t('locationSection.provinces.trang'), link: buildLocationLink('ตรัง') },
        { id: 44, name: t('locationSection.provinces.narathiwat'), link: buildLocationLink('นราธิวาส') },
        { id: 45, name: t('locationSection.provinces.pattani'), link: buildLocationLink('ปัตตานี') },
        { id: 46, name: t('locationSection.provinces.krabi'), link: buildLocationLink('กระบี่') },
        { id: 47, name: t('locationSection.provinces.chumphon'), link: buildLocationLink('ชุมพร') },
        { id: 48, name: t('locationSection.provinces.phatthalung'), link: buildLocationLink('พัทลุง') }
      ]
    }
  ];

  const popularLocations = useMemo(() => [
    { id: 1, name: t('locationSection.provinces.bangkok'), image: '/assets/homePage/location/bangkok.webp', count: getCountByProvinceName('กรุงเทพมหานคร'), locationKey: null, provinceName: 'กรุงเทพมหานคร' },
    { id: 2, name: t('locationSection.provinces.pathumthani'), image: '/assets/homePage/location/pathumthani.webp', count: getCountByProvinceName('ปทุมธานี'), locationKey: null, provinceName: 'ปทุมธานี' },
    { id: 3, name: t('locationSection.provinces.samutprakan'), image: '/assets/homePage/location/samutprakan.webp', count: getCountByProvinceName('สมุทรปราการ'), locationKey: null, provinceName: 'สมุทรปราการ' },
    { id: 4, name: t('locationSection.provinces.nonthaburi'), image: '/assets/homePage/location/nonthaburi.webp', count: getCountByProvinceName('นนทบุรี'), locationKey: null, provinceName: 'นนทบุรี' },
    { id: 5, name: t('locationSection.provinces.nakhonpathom'), image: '/assets/homePage/location/nakhonpathom.webp', count: getCountByProvinceName('นครปฐม'), locationKey: null, provinceName: 'นครปฐม' },
    { id: 6, name: t('locationSection.provinces.chonburi'), image: '/assets/homePage/location/chonburi.webp', count: getCountByProvinceName('ชลบุรี'), locationKey: null, provinceName: 'ชลบุรี' },
    { id: 7, name: t('locationSection.provinces.ayutthaya'), image: '/assets/homePage/location/ayutthaya.webp', count: getCountByProvinceName('พระนครศรีอยุธยา'), locationKey: null, provinceName: 'พระนครศรีอยุธยา' },
    { id: 8, name: t('locationSection.provinces.samutsakhon'), image: '/assets/homePage/location/samutsakhon.webp', count: getCountByProvinceName('สมุทรสาคร'), locationKey: null, provinceName: 'สมุทรสาคร' },
    { id: 9, name: t('locationSection.provinces.nakhonsawan'), image: '/assets/homePage/location/nakhonsawan.webp', count: getCountByProvinceName('นครสวรรค์'), locationKey: null, provinceName: 'นครสวรรค์' },
    { id: 10, name: t('locationSection.provinces.chaam'), image: '/assets/homePage/location/huaHinChaAm.webp', count: getCountByProvinceName('ชะอำ'), locationKey: 'chaam', provinceName: 'ชะอำ' },
    { id: 11, name: t('locationSection.provinces.pattaya'), image: '/assets/homePage/location/pattaya.webp', count: getCountByProvinceName('พัทยา'), locationKey: 'pattaya', provinceName: 'พัทยา' },
    { id: 12, name: t('locationSection.provinces.rayong'), image: '/assets/homePage/location/rayong.webp', count: getCountByProvinceName('ระยอง'), locationKey: null, provinceName: 'ระยอง' },
    { id: 13, name: t('locationSection.provinces.chiangmai'), image: '/assets/homePage/location/chiangmai.webp', count: getCountByProvinceName('เชียงใหม่'), locationKey: null, provinceName: 'เชียงใหม่' },
    { id: 15, name: t('locationSection.provinces.huahin'), image: '/assets/homePage/location/huahin.webp', count: getCountByProvinceName('หัวหิน'), locationKey: 'huahin', provinceName: 'หัวหิน' },
    { id: 16, name: t('locationSection.provinces.phuket'), image: '/assets/homePage/location/phuket.webp', count: getCountByProvinceName('ภูเก็ต'), locationKey: null, provinceName: 'ภูเก็ต' },
    { id: 17, name: t('locationSection.provinces.songkhla'), image: '/assets/homePage/location/songkhla.webp', count: getCountByProvinceName('สงขลา'), locationKey: null, provinceName: 'สงขลา' },
    { id: 18, name: t('locationSection.provinces.nakhonratchasima'), image: '/assets/homePage/location/nakhonrat.webp', count: getCountByProvinceName('นครราชสีมา'), locationKey: null, provinceName: 'นครราชสีมา' },
    { id: 19, name: t('locationSection.provinces.khonkaen'), image: '/assets/homePage/location/khonkaen.webp', count: getCountByProvinceName('ขอนแก่น'), locationKey: null, provinceName: 'ขอนแก่น' }
  ], [popularLocationsData, locale, t]);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: isMobile ? 1 : (isTablet ? 3 : 6), // แสดง 4 รายการใน tablet
    slidesToScroll: isMobile ? 1 : (isTablet ? 3 : 6),
    initialSlide: 0,
    draggable: true,
    swipe: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: isTablet ? 3 : 4, // แสดง 4 รายการใน tablet
          slidesToScroll: isTablet ? 3 : 4,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        }
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  const handleViewAll = () => {
    router.push('/locations');
  };


  return (
    <div className={styles.locationSectionContainer}>
      <div style={{ maxWidth: "1496px", margin: "0 auto", padding: isTablet ? "0 18px" : "0 24px" }}>
        <Typography sx={{
          fontSize: isTablet ? '28px' : '33px', // ลดขนาดหัวข้อใน tablet
          fontWeight: '500',
          marginBottom: isTablet ? '18px' : '24px',
          fontFamily: "'Prompt', 'Inter', 'Noto Serif SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        }}>
          {t('locationSection.title')}
        </Typography>
        
        <div className={styles.regionWrapper}>
          {/* Accordion Section */}
          <div className={styles.collapseWrapper}>
            {regions.map((region, index) => (
              <StyledAccordion
                key={index}
                expanded={expanded === index.toString()}
                onChange={handleAccordionChange(index.toString())}
              >
                <AccordionSummary
                  expandIcon={expanded === index.toString() ? 
                    <KeyboardArrowDownIcon sx={{ color: '#F4762A', fontSize: 20 }} /> : 
                    <KeyboardArrowDownIcon sx={{ color: '#666', fontSize: 20 }} />
                  }
                >
                  <Typography>{region.name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: isTablet ? 0.5 : 1 // ลด gap ใน tablet
                  }}>
                    {region.provinces.map(province => (
                      <StyledChip
                        key={province.id}
                        label={province.name}
                        onClick={() => router.push(province.link)}
                        icon={<ArrowForwardIosOutlinedIcon sx={{ 
                          fontSize: isTablet ? 10 : 12 
                        }} />}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </StyledAccordion>
            ))}
          </div>
          
          {/* Region Image Section */}
          <div className={styles.regionImageContainer}>
            {regions.map((region, index) => (
              <div 
                key={index}
                className={`${styles.regionImage} ${expanded === index.toString() ? styles.active : ''}`}
              >
                <Image 
                  src={region.image} 
                  alt={region.name}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 50vw"
                  style={{ objectFit: 'cover' }}
                />
              </div>
            ))}
          </div>
        </div>

        <Box sx={{ 
          maxWidth: '100%', 
          padding: '0',
          position: 'relative',
          marginTop: isTablet ? '30px' : '40px', // ลด margin ใน tablet
        }}>
          <StyledCarousel {...settings}>
            {popularLocations.map((location) => (
              <div key={location.id}>
                <ItemCard 
                  onClick={() => handleLocationClick(location.locationKey, location.provinceName)}
                  sx={{ cursor: 'pointer' }}
                >
                  <ImageContainer>
                    <ItemImage src={location.image} alt={location.name} />
                  </ImageContainer>
                  <ItemContent>
                    <ItemTitle>{location.name}</ItemTitle>
                    <ItemCount>
                      <span className="count">{location.count.toLocaleString()}</span> {t('locationSection.listings')}
                    </ItemCount>
                  </ItemContent>
                </ItemCard>
              </div>
            ))}
          </StyledCarousel>
        </Box>
      </div>
    </div>
  );
};

export default LocationSection; 